import {
  AddCircleOutline,
  Article,
  CancelOutlined,
  CloudDownload,
  DeleteOutline,
  Edit,
  PlaylistAddCheck,
  Print,
} from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import React, { ComponentProps, PropsWithChildren } from 'react';
import { Link, To } from 'react-router-dom';

type IconKey = 'view' | 'edit' | 'delete' | 'cancel' | 'download' | 'add' | 'print' | 'check';

const ICONS: Record<IconKey, { component: React.ElementType; color: string }> = {
  view: { component: Article, color: '#17a2b8' },
  add: { component: AddCircleOutline, color: '#28a745' },
  edit: { component: Edit, color: '#ffc107' },
  delete: { component: DeleteOutline, color: '#dc3545' },
  cancel: { component: CancelOutlined, color: '#dc3545' },
  download: { component: CloudDownload, color: '#007bff' },
  print: { component: Print, color: '#6c757d' },
  check: { component: PlaylistAddCheck, color: '#20c997' },
};

type Props = Pick<ComponentProps<typeof Tooltip>, 'placement'> &
  Omit<ComponentProps<typeof IconButton>, 'children'> & {
    to?: To;
    title?: string;
    icon?: IconKey;
    color?: string;
    className?: string;
    children?: React.ReactNode;
  };

const IconAction: React.FC<PropsWithChildren<Props>> = ({
  title,
  to,
  icon,
  color,
  className,
  placement = 'left',
  children,
  ...iconButtonProps
}) => {
  const IconComponent = icon ? ICONS[icon].component : null;
  const iconColor = color ?? (icon ? ICONS[icon].color : undefined);

  const iconContent = IconComponent ? <IconComponent className={className} style={{ color: iconColor }} /> : children;

  const commonProps = {
    className,
    children: iconContent,
  };

  const button = to ? (
    <IconButton component={Link} to={to} {...commonProps} />
  ) : (
    <IconButton {...commonProps} {...iconButtonProps} />
  );

  return (
    <Tooltip title={title} placement={placement}>
      {button}
    </Tooltip>
  );
};

export default IconAction;
